import type { Course, PerHoleData, Player, Round, Score, Tournament } from "./types.js";
import { holesPlayedCount } from "./handicap.js";
import { getFormat } from "./formats/index.js";
import type { Contest } from "./formats/types.js";
import {
  computeHandicaps,
  buildScoreMap,
  grossOnHole,
  holesInSegment,
  parByHole,
  scoringHoles,
} from "./formats/util.js";
import { computeLiveRoundResults, scorableHoles, type LiveRoundResult } from "./leaderboard.js";

/**
 * Working out *why* a player scored what they did.
 *
 * The app was happy to tell you the answer and not the arithmetic: handicap strokes
 * appear from nowhere, a round total turns into points by a rule you can't see, and a
 * total is the sum of rounds you have to reconstruct yourself. All of that is already
 * computed somewhere — this reads the same inputs the scorer read and reports the
 * steps rather than just the result.
 *
 * Deliberately derived, never a second implementation: the points here come from the
 * round result the leaderboard uses, so a breakdown can't disagree with the board it
 * was opened from. Where a format knows something the engine can't (which hole a skin
 * was won on), it supplies it through `Format.explainHoles`.
 */

/** One line of arithmetic. `emphasis` marks the line that is the answer. */
export interface BreakdownRow {
  label: string;
  value: string;
  note?: string;
  emphasis?: boolean;
}

export interface HoleLine {
  hole: number;
  par: number;
  strokeIndex: number;
  gross: number | null;
  /** Handicap strokes received on this hole. */
  received: number;
  net: number | null;
  /** What the format did with this hole, when it has something to say. */
  tag?: string;
}

export interface RoundExplanation {
  roundId: string;
  roundName: string;
  formatName: string;
  /** Null when the round has no course yet, in which case there is nothing to show. */
  courseName: string | null;
  teeName: string | null;
  handicap: {
    index: number;
    course: number;
    playing: number;
    allowancePercent: number;
    halvedForNineHoles: boolean;
  } | null;
  holes: HoleLine[];
  /** How the round's own result was arrived at. */
  summary: BreakdownRow[];
  /** The format's generated rules, so the reasoning and the rules sit together. */
  rules: string[];
  points: number;
  provisional: boolean;
}

export interface TotalExplanation {
  playerId: string;
  playerName: string;
  rounds: {
    roundId: string;
    roundName: string;
    points: number;
    multiplier: number;
    provisional: boolean;
    /** False when nobody has posted a score in this round at all. */
    started: boolean;
    holesScored: number;
    holesTotal: number;
    /** Why this round contributes what it does, when that isn't obvious. */
    note?: string;
  }[];
  /** Confirmed points — closed rounds only, matching the standings. */
  total: number;
  /** Points from rounds still open, shown separately exactly as the board does. */
  projected: number;
  placement: number | null;
  tiebreaker: Tournament["finalTiebreaker"];
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th");
  return `${n}${suffix}`;
}

/**
 * The arithmetic behind one player's result in one round.
 *
 * `result` is the round result already computed for the leaderboard; passing it in
 * rather than recomputing is what guarantees the breakdown and the board agree.
 */
export function explainRound(
  round: Round,
  course: Course | undefined,
  players: Player[],
  scores: Score[],
  perHoleData: PerHoleData[],
  result: LiveRoundResult,
  playerId: string,
): RoundExplanation {
  const format = getFormat(round.formatId);
  const player = players.find((p) => p.id === playerId);
  const teeSet = course?.teeSets.find((t) => t.name === round.teeSet);
  const points = result.points[playerId] ?? 0;

  const base: RoundExplanation = {
    roundId: round.id,
    roundName: round.name || "Untitled round",
    formatName: format.name,
    courseName: course?.name ?? null,
    teeName: teeSet?.name ?? null,
    handicap: null,
    holes: [],
    summary: [],
    rules: format.describeRules(round, players, teeSet),
    points,
    provisional: result.provisional,
  };

  if (!course || !teeSet || !player) {
    base.summary.push({
      label: "No course set",
      value: "—",
      note: "This round has no course or tee set yet, so there is nothing to score.",
    });
    return base;
  }

  // Every round is played over the same hole numbers, and buildScoreMap keys on
  // player+hole with no notion of which round a score belongs to — so this has to be
  // narrowed first or a two-round game explains round one using round two's card.
  const roundScores = scores.filter((s) => s.roundId === round.id);

  const handicaps = computeHandicaps(round, teeSet, players);
  const info = handicaps.get(playerId);
  const scoreMap = buildScoreMap(roundScores);
  const pars = parByHole(teeSet, round);
  const holeNumbers = scoringHoles(round, teeSet);

  // Only holes everyone has finished count toward a live result, so the breakdown has
  // to mark the rest rather than quietly including them.
  const counted = new Set(scorableHoles(round, course, players, roundScores));

  const roundPerHole = perHoleData
    .filter((d) => d.roundId === round.id)
    .map((d) => ({ hole: d.hole, ...d.data }));
  const tags =
    format.explainHoles?.({
      round,
      course: teeSet,
      players,
      scores: roundScores,
      perHole: roundPerHole,
      playerId,
    }) ?? new Map<number, string>();

  base.handicap = {
    index: player.handicapIndex,
    course: info?.courseHandicap ?? 0,
    playing: info?.playingHandicap ?? 0,
    allowancePercent: round.handicapAllowance,
    halvedForNineHoles: round.holeSet !== "all18",
  };

  base.holes = holeNumbers.map((hole) => {
    const gross = grossOnHole(scoreMap, playerId, hole);
    const received = info?.strokesByHole.get(hole) ?? 0;
    return {
      hole,
      par: pars.get(hole) ?? 0,
      strokeIndex: teeSet.holes.find((h) => h.number === hole)?.strokeIndex ?? 0,
      gross,
      received,
      net: gross == null ? null : gross - received,
      tag: tags.get(hole) ?? (gross != null && !counted.has(hole) ? "not counted yet" : undefined),
    };
  });

  base.summary = summarise(round, players, result, playerId, base.holes, counted.size);
  return base;
}

/** The steps between "here are your holes" and "here are your points". */
function summarise(
  round: Round,
  players: Player[],
  result: LiveRoundResult,
  playerId: string,
  holes: HoleLine[],
  countedHoles: number,
): BreakdownRow[] {
  const rows: BreakdownRow[] = [];
  const format = getFormat(round.formatId);
  const points = result.points[playerId] ?? 0;

  const played = holes.filter((h) => h.gross != null);
  if (played.length > 0) {
    const gross = played.reduce((sum, h) => sum + (h.gross ?? 0), 0);
    const received = played.reduce((sum, h) => sum + h.received, 0);
    rows.push({
      label: "Gross strokes",
      value: fmt(gross),
      note: `over ${played.length} hole${played.length === 1 ? "" : "s"} played`,
    });
    if (received > 0) {
      rows.push({ label: "Handicap strokes", value: `−${fmt(received)}`, note: "allocated by stroke index" });
      rows.push({ label: "Net strokes", value: fmt(gross - received) });
    }
  }

  if (countedHoles < holes.length) {
    rows.push({
      label: "Holes counted",
      value: `${countedHoles} of ${holes.length}`,
      note: "a hole counts once every player on it has a score",
    });
  }

  // The round's own headline figure, whatever the format calls it. Which field it
  // lands in also says which way it sorts, so nothing here needs to know the format
  // by name.
  const totals = result.pointsTotal ?? result.netTotal ?? result.grossTotal;
  const lowerIsBetter = !result.pointsTotal;
  const total = totals?.[playerId];
  if (total != null) {
    rows.push({
      label: result.pointsTotal ? "Points total" : result.netTotal ? "Net total" : "Gross total",
      value: fmt(total),
    });
  }

  // Where the format ranks players against each other, say where this one came.
  if (format.uses.pointTable && total != null) {
    const ranking = players
      .map((p) => ({ id: p.id, value: totals?.[p.id] }))
      .filter((e): e is { id: string; value: number } => e.value != null);
    const better = ranking.filter((e) =>
      lowerIsBetter ? e.value < total : e.value > total,
    ).length;
    const tied = ranking.filter((e) => e.value === total && e.id !== playerId).length;
    rows.push({
      label: `Finished ${ordinal(better + 1)}`,
      value: `of ${ranking.length}`,
      note:
        tied > 0
          ? `tied with ${tied} other${tied === 1 ? "" : "s"}, so those places' points are shared`
          : `points by place: ${round.pointTable.join(", ")}`,
    });
  }

  // Segmented formats score each segment as its own match; the per-segment points are
  // already in the result, so show them adding up.
  if (result.segments && result.segments.length > 0) {
    for (const seg of result.segments) {
      const segPoints = seg.points[playerId];
      const partial = seg.holesScored < seg.holesTotal;
      rows.push({
        label: `Holes ${seg.startHole}–${seg.endHole}`,
        value: segPoints == null ? "—" : `${fmt(segPoints)} pts`,
        // A segment pays as soon as every side has posted, so a running result and a
        // finished one look identical without saying how much of it is actually in.
        note:
          segPoints == null
            ? "not scored yet"
            : partial
              ? `${seg.holesScored} of ${seg.holesTotal} holes so far`
              : undefined,
      });
    }
  }

  if (round.multiplier !== 1 && Number.isFinite(round.multiplier)) {
    rows.push({
      label: "Round multiplier",
      value: `×${fmt(round.multiplier)}`,
      note: "already included in the points below",
    });
  }

  rows.push({ label: "Points this round", value: fmt(points), emphasis: true });
  return rows;
}

/** One player's own ball inside a column. */
export interface RoundGridMember {
  playerId: string;
  gross: number | null;
  received: number;
  net: number | null;
  /** True when this is the ball the column's `value` came from (four-ball's better net). */
  counts: boolean;
}

/** One column's standing on one hole: a player's score, or a side's. */
export interface RoundGridCell {
  columnId: string;
  /**
   * Each ball played into this column. One entry for an individual column; two for a
   * side whose players hole out separately; none for a single-ball side, where there is
   * no per-player ball and `value` is the whole story.
   */
  members: RoundGridMember[];
  /** The number this column is judged on, on `RoundBreakdown.basis`. */
  value: number | null;
  /** Low on this hole. Only ever set on a hole the scoring counts. */
  best: boolean;
}

/** A player or a side — whichever this round is played and read in. */
export interface RoundGridColumn {
  id: string;
  label: string;
  playerIds: string[];
}

export interface RoundGridRow {
  hole: number;
  par: number;
  strokeIndex: number;
  /** In the section's `columns` order. */
  cells: RoundGridCell[];
  /** False while the hole is missing a score the format needs. */
  counted: boolean;
}

/**
 * A run of holes played by one set of columns.
 *
 * Sides are per segment, and a partner-swap round changes them partway through — so a
 * single table of columns can't describe the round. One section per segment can, and a
 * round with one set of sides (or none) is just the one section.
 */
/** What a column added up to over a section, and what that was worth. */
export interface RoundGridTotal {
  columnId: string;
  /** Sum of the column's per-hole values — the number the format judges it on. */
  value: number | null;
  /** Sum of the members' gross. Null for a side that shares one ball. */
  gross: number | null;
  /** How many of the section's holes this column has a value for. */
  holesIn: number;
  /**
   * Points this column earned: the segment's own where the section is a segment, the
   * round's where it covers the round. Null when the segment hasn't scored yet — which
   * is not the same as zero, and must not render as one.
   */
  points: number | null;
}

export interface RoundGridSection {
  /** Null when the round never changes columns, so there is nothing to distinguish. */
  title: string | null;
  columns: RoundGridColumn[];
  rows: RoundGridRow[];
  /** In `columns` order. */
  totals: RoundGridTotal[];
  /** Holes this section covers, against which each total's `holesIn` reads. */
  holesTotal: number;
}

export interface RoundBreakdown {
  roundId: string;
  roundName: string;
  formatName: string;
  courseName: string | null;
  teeName: string | null;
  /** True when the columns are sides rather than individual players. */
  bySide: boolean;
  /** Which number `best` and `value` are, so the screen can say which it is. */
  basis: "net" | "gross";
  sections: RoundGridSection[];
  /**
   * What each player took from the round overall, across every segment. A partner-swap
   * round pays per segment, so no single section says what a player went home with.
   */
  roundPoints: { playerId: string; name: string; points: number }[];
  contests: Contest[];
  rules: string[];
  provisional: boolean;
}

/**
 * The whole field, hole by hole.
 *
 * `explainRound` answers "why did *I* score what I scored", and its per-hole notes come
 * from `Format.explainHoles`, which takes a player and returns strings. Neither can say
 * who won a hole — so match play, whose entire result is exactly that, had a blank
 * column, and Wolf could only describe a hole from one seat.
 *
 * Two layers, because they fail differently. The grid is engine-level and works for every
 * format: each column's number hole by hole, with the low one marked. The contests come
 * from `Format.explainContests`, because who won a hole is the format's own business —
 * and a format with no contest to describe simply returns none.
 *
 * A round played in sides is read in sides. The columns are the segment's teams, and each
 * side's number comes from `Format.sideValueOnHole` — the format's own, so a scramble
 * side is shown off its combined handicap and a four-ball side off its better ball.
 */
export function explainRoundBreakdown(
  round: Round,
  course: Course | undefined,
  players: Player[],
  scores: Score[],
  perHoleData: PerHoleData[],
  result: LiveRoundResult,
): RoundBreakdown {
  const format = getFormat(round.formatId);
  const teeSet = course?.teeSets.find((t) => t.name === round.teeSet);

  const base: RoundBreakdown = {
    roundId: round.id,
    roundName: round.name || "Untitled round",
    formatName: format.name,
    courseName: course?.name ?? null,
    teeName: teeSet?.name ?? null,
    bySide: false,
    // `uses.handicapAllowance` is already the "this format plays off handicap" flag —
    // false for gross stroke play and for scramble, which derives its own team handicap.
    // Reading it beats naming formats here, which is what that field exists to avoid.
    basis: format.uses.handicapAllowance ? "net" : "gross",
    sections: [],
    roundPoints: players.map((p) => ({
      playerId: p.id,
      name: p.name,
      points: result.points[p.id] ?? 0,
    })),
    contests: [],
    rules: format.describeRules(round, players, teeSet),
    provisional: result.provisional,
  };

  if (!course || !teeSet) return base;

  // Same narrowing as explainRound: buildScoreMap keys on player+hole with no notion of
  // which round a score belongs to, so a two-round game would grid round one's holes
  // using round two's card.
  const roundScores = scores.filter((s) => s.roundId === round.id);
  const roundPerHole = perHoleData
    .filter((d) => d.roundId === round.id)
    .map((d) => ({ hole: d.hole, ...d.data }));

  const handicaps = computeHandicaps(round, teeSet, players);
  const scoreMap = buildScoreMap(roundScores);
  const pars = parByHole(teeSet, round);
  const holeNumbers = scoringHoles(round, teeSet);
  const done = scorableHoles(round, course, players, roundScores);
  const counted = new Set(done);
  const nameOf = (id: string) => players.find((p) => p.id === id)?.name ?? id;

  /** A player's own ball. `counts` is filled in once the column's value is known. */
  const memberOn = (playerId: string, hole: number): RoundGridMember => {
    const gross = grossOnHole(scoreMap, playerId, hole);
    const received = handicaps.get(playerId)?.strokesByHole.get(hole) ?? 0;
    return {
      playerId,
      gross,
      received,
      net: gross == null ? null : gross - received,
      counts: false,
    };
  };

  // A side's number is the format's to decide — better ball, or one shared ball off a
  // combined handicap — and declaring `sideValueOnHole` is also what says the format
  // plays in sides at all. Without segments there are no sides to read, whatever the
  // format thinks.
  const sideValue = format.sideValueOnHole?.({
    round,
    course: teeSet,
    players,
    scores: roundScores,
    perHole: roundPerHole,
  });
  const sidesOf = (segment: Round["segments"][number]) =>
    segment.teams.filter((t) => t.length > 0);
  base.bySide =
    Boolean(sideValue) && round.segments.some((segment) => sidesOf(segment).length > 0);

  // `uses.handicapAllowance` answers "does this format read round.handicapAllowance",
  // which is not the same question once sides are involved: scramble derives a combined
  // team handicap from its own percentages and nets the side's ball against it, so
  // reading the flag would label a net score "gross". Every format that declares
  // sideValueOnHole nets its side's score, so in sides the basis is always net.
  if (base.bySide) base.basis = "net";

  /**
   * The points a segment paid, found by its hole bounds rather than its position.
   *
   * `scoreSegmentsByTotal` skips a segment with fewer than two sides *before* pushing its
   * result (`formats/util.ts`), so `result.segments[i]` is not `round.segments[i]` — while
   * match play does push one per segment, which is exactly what would make an index-based
   * read look correct until a real round had a lone side in it. Both carry the bounds.
   *
   * Every member of a side is assigned the same figure, so the first member's entry is the
   * side's. An unscored segment leaves the record empty, and `undefined` there means "not
   * scored yet", which is not zero.
   */
  const segmentPoints = (segment: Round["segments"][number], playerId: string) => {
    const scored = result.segments?.find(
      (s) => s.startHole === segment.startHole && s.endHole === segment.endHole,
    );
    return scored?.points[playerId] ?? null;
  };

  type PendingSection = {
    title: string | null;
    columns: RoundGridColumn[];
    holes: number[];
    /** Null for a section that covers the whole round rather than one segment. */
    segment: Round["segments"][number] | null;
  };

  const sections: PendingSection[] = base.bySide
    ? round.segments
        .map((segment) => ({
          segment,
          columns: sidesOf(segment).map((team, i) => ({
            id: String(i),
            label: team.map(nameOf).join(" & "),
            playerIds: team,
          })),
          holes: holesInSegment(holeNumbers, segment.startHole, segment.endHole),
        }))
        .filter((s) => s.columns.length > 0 && s.holes.length > 0)
        .map((s, _i, all) => ({
          // Only worth naming when there is more than one to tell apart.
          title:
            all.length > 1 ? `Holes ${s.holes[0]}–${s.holes[s.holes.length - 1]}` : null,
          ...s,
        }))
    : [
        {
          title: null,
          columns: players.map((p) => ({ id: p.id, label: p.name, playerIds: [p.id] })),
          holes: holeNumbers,
          segment: null,
        },
      ];

  base.sections = sections.map((section) => {
    const rows = section.holes.map((hole) => {
      const cells: RoundGridCell[] = section.columns.map((column) => {
        // A single-ball side has no per-player ball to show: the one score is recorded
        // against whichever member entered it, so listing it under that player would be
        // reporting who held the phone.
        const members =
          base.bySide && format.entry === "perTeam"
            ? []
            : column.playerIds.map((id) => memberOn(id, hole));

        const value = base.bySide
          ? sideValue!(column.playerIds, hole)
          : base.basis === "gross"
            ? (members[0]?.gross ?? null)
            : (members[0]?.net ?? null);

        // Which ball the side is actually counting — four-ball's better net.
        if (value != null && members.length > 1) {
          for (const m of members) if (m.net === value) m.counts = true;
        }

        return { columnId: column.id, members, value, best: false };
      });

      // Only on a hole the scoring would actually count. Marking a low score while half
      // the field is still walking up makes whoever entered first look like they won it.
      if (counted.has(hole)) {
        const posted = cells.filter((c) => c.value != null);
        if (posted.length > 0) {
          const low = Math.min(...posted.map((c) => c.value!));
          for (const cell of cells) if (cell.value === low) cell.best = true;
        }
      }

      return {
        hole,
        par: pars.get(hole) ?? 0,
        strokeIndex: teeSet.holes.find((h) => h.number === hole)?.strokeIndex ?? 0,
        cells,
        counted: counted.has(hole),
      };
    });

    // Summed from the cells above rather than recomputed, so a column's total can only
    // ever be the numbers printed over it. It also has to be derived: `netTotal` and
    // `grossTotal` are set by the stroke-play formats alone — every team format, match
    // play, skins and Wolf leave them undefined.
    const totals: RoundGridTotal[] = section.columns.map((column, i) => {
      const cells = rows.map((row) => row.cells[i]!);
      const posted = cells.filter((c) => c.value != null);
      const grossBalls = cells.flatMap((c) => c.members).filter((m) => m.gross != null);

      return {
        columnId: column.id,
        value: posted.length > 0 ? posted.reduce((sum, c) => sum + c.value!, 0) : null,
        // A single-ball side has no per-player gross to add up.
        gross:
          grossBalls.length > 0 ? grossBalls.reduce((sum, m) => sum + m.gross!, 0) : null,
        holesIn: posted.length,
        points: section.segment
          ? segmentPoints(section.segment, column.playerIds[0]!)
          : (result.points[column.playerIds[0]!] ?? 0),
      };
    });

    return {
      title: section.title,
      columns: section.columns,
      rows,
      totals,
      holesTotal: rows.length,
    };
  });

  // Scored over exactly what the leaderboard scored over, so the contest and the points
  // it produced can't tell different stories — the same reason `result` is passed into
  // `explainRound` rather than recomputed. `computeLiveRoundResults` withholds
  // `onlyHoles` on a full card, and so does this.
  const fullCard = done.length === holesPlayedCount(teeSet.holes, round.holeSet);
  base.contests =
    format.explainContests?.({
      round,
      course: teeSet,
      players,
      scores: roundScores,
      perHole: roundPerHole,
      settled: !result.provisional,
      ...(fullCard ? {} : { onlyHoles: done }),
    }) ?? [];

  return base;
}

/** How a player's confirmed total was built, round by round. */
export function explainTotal(
  tournament: Tournament,
  courses: Map<string, Course>,
  scores: Score[],
  perHoleData: PerHoleData[],
  playerId: string,
  placement: number | null,
): TotalExplanation {
  const results = computeLiveRoundResults(tournament, courses, scores, perHoleData);

  const rounds = tournament.rounds.map((round, i) => {
    const result = results[i];
    const points = result?.points[playerId] ?? 0;
    const provisional = Boolean(result?.provisional);
    const started = (result?.holesScored ?? 0) > 0;

    // A round nobody has teed off in isn't provisional — there is nothing to be
    // provisional about — so it would otherwise sit in the list contributing zero and
    // looking exactly like a closed round that paid nothing.
    const note = !started
      ? round.locked
        ? "closed with no scores"
        : "not started"
      : provisional
        ? "still open — counts once the round is closed"
        : undefined;

    return {
      roundId: round.id,
      roundName: round.name || "Untitled round",
      points,
      multiplier: round.multiplier,
      provisional,
      started,
      holesScored: result?.holesScored ?? 0,
      holesTotal: result?.holesTotal ?? 0,
      note,
    };
  });

  return {
    playerId,
    playerName: tournament.players.find((p) => p.id === playerId)?.name ?? playerId,
    rounds,
    total: rounds.filter((r) => !r.provisional).reduce((sum, r) => sum + r.points, 0),
    projected: rounds.filter((r) => r.provisional).reduce((sum, r) => sum + r.points, 0),
    placement,
    tiebreaker: tournament.finalTiebreaker,
  };
}
